import { MouseEvent, ReactNode, forwardRef, useState } from "react";

import {
  IconButton,
  IconButtonProps,
  Popover,
  PopoverProps,
  Tooltip,
  TooltipProps,
} from "@mui/material";

const NAME = "IconButtonPopover";

export interface IconButtonPopoverProps extends IconButtonProps {
  tooltip?: ReactNode;
  tooltipProps?: Omit<TooltipProps, "title">;
}

export const IconButtonPopover = forwardRef<
  HTMLButtonElement,
  IconButtonPopoverProps & {
    popover: ReactNode;
    popoverProps?: Partial<PopoverProps>;
  }
>(
  (
    { onClick, tooltip, tooltipProps, popover, popoverProps, ...buttonProps },
    buttonRef,
  ) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
      onClick?.(e);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
      <>
        {tooltip ? (
          <Tooltip title={tooltip} {...tooltipProps}>
            <IconButton
              onClick={handleClick}
              ref={buttonRef}
              {...buttonProps}
            />
          </Tooltip>
        ) : (
          <IconButton onClick={handleClick} ref={buttonRef} {...buttonProps} />
        )}
        <Popover
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          open={open}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          {...popoverProps}
          onClose={(e, reason) => {
            handleClose();
            if (popoverProps?.onClose) popoverProps?.onClose(e, reason);
          }}
        >
          {popover}
        </Popover>
      </>
    );
  },
);
IconButtonPopover.displayName = NAME;
